import { createTheme } from "@material-ui/core/styles";

import {
  neutral,
  brand,
  accent,
  semantic,
  radius,
  spacingUnit,
  fontFamily,
  fontFamilyMono,
  layout,
} from "./tokens";
import { alpha } from "./color";

export * from "./tokens";

/**
 * Sombras suaves e de baixo contraste. MUI v4 exige exatamente 25 níveis;
 * na prática só usamos os 4 primeiros — elevação alta é ruído em software
 * de operação.
 */
const buildShadows = (isDark) => {
  const c = isDark ? "0, 0, 0" : "16, 24, 40";
  const scale = [
    "none",
    `0 1px 2px rgba(${c}, 0.06)`,
    `0 1px 3px rgba(${c}, 0.08), 0 1px 2px rgba(${c}, 0.04)`,
    `0 2px 6px rgba(${c}, 0.08)`,
    `0 4px 10px rgba(${c}, 0.08)`,
    `0 6px 16px rgba(${c}, 0.10)`,
    `0 8px 24px rgba(${c}, 0.12)`,
    `0 12px 32px rgba(${c}, 0.14)`,
  ];
  const out = scale.slice();
  while (out.length < 25) out.push(scale[scale.length - 1]);
  return out;
};

const buildPalette = (isDark) => {
  if (isDark) {
    return {
      type: "dark",
      primary: {
        main: accent.onDark,
        dark: accent.light,
        light: "#93C5FD",
        contrastText: neutral[950],
      },
      secondary: { main: brand.gold, contrastText: neutral[950] },
      success: { main: semantic.success.onDark, contrastText: neutral[950] },
      warning: { main: semantic.warning.onDark, contrastText: neutral[950] },
      error: { main: semantic.danger.onDark, contrastText: neutral[950] },
      info: { main: semantic.info.onDark, contrastText: neutral[950] },
      background: { default: neutral[950], paper: neutral[900] },
      text: {
        primary: neutral[100],
        secondary: neutral[400],
        disabled: neutral[600],
        hint: neutral[500],
      },
      divider: "rgba(148, 163, 184, 0.18)",
      action: {
        hover: "rgba(148, 163, 184, 0.08)",
        selected: "rgba(96, 165, 250, 0.16)",
        disabled: neutral[600],
        disabledBackground: "rgba(148, 163, 184, 0.12)",
      },
    };
  }
  return {
    type: "light",
    primary: {
      main: accent.main,
      dark: accent.hover,
      light: accent.light,
      contrastText: neutral[0],
    },
    secondary: { main: brand.navy, contrastText: neutral[0] },
    success: { main: semantic.success.main, contrastText: neutral[0] },
    warning: { main: semantic.warning.main, contrastText: neutral[0] },
    error: { main: semantic.danger.main, contrastText: neutral[0] },
    info: { main: semantic.info.main, contrastText: neutral[0] },
    background: { default: neutral[50], paper: neutral[0] },
    text: {
      primary: neutral[900],
      secondary: neutral[500],
      disabled: neutral[400],
      hint: neutral[400],
    },
    divider: neutral[200],
    action: {
      hover: alpha(neutral[900], 0.04),
      selected: accent.soft,
      disabled: neutral[400],
      disabledBackground: neutral[100],
    },
  };
};

const buildTypography = () => ({
  fontFamily,
  fontSize: 14,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: { fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.02em" },
  h2: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.015em" },
  h3: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.01em" },
  h4: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.45 },
  subtitle1: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.45 },
  subtitle2: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.45 },
  body1: { fontSize: "0.875rem", lineHeight: 1.55 },
  body2: { fontSize: "0.8125rem", lineHeight: 1.55 },
  caption: { fontSize: "0.75rem", lineHeight: 1.45 },
  // textTransform none: UPPERCASE em botão é a assinatura mais óbvia de
  // Material 2014 e some daqui globalmente.
  button: { fontSize: "0.875rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  overline: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    lineHeight: 1.4,
  },
});

const buildOverrides = (palette, isDark) => {
  const surfaceSubtle = isDark ? neutral[800] : neutral[50];
  const borderColor = palette.divider;

  return {
    MuiCssBaseline: {
      "@global": {
        html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? neutral[700] : neutral[300],
          borderRadius: radius.pill,
          border: `2px solid ${palette.background.default}`,
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: isDark ? neutral[600] : neutral[400],
        },
      },
    },

    MuiPaper: {
      rounded: { borderRadius: radius.lg },
      outlined: { border: `1px solid ${borderColor}` },
    },

    MuiButton: {
      root: {
        borderRadius: radius.md,
        padding: "6px 14px",
        minHeight: 36,
      },
      contained: {
        boxShadow: "none",
        "&:hover": { boxShadow: "none" },
        "&:active": { boxShadow: "none" },
      },
      containedPrimary: {
        "&:hover": { backgroundColor: isDark ? accent.light : accent.hover },
      },
      outlined: {
        borderColor,
        "&:hover": { borderColor: isDark ? neutral[600] : neutral[300] },
      },
      sizeSmall: { minHeight: 30, padding: "4px 10px", fontSize: "0.8125rem" },
    },

    MuiIconButton: {
      root: { borderRadius: radius.md },
      sizeSmall: { padding: 6 },
    },

    MuiTableCell: {
      root: {
        borderBottom: `1px solid ${borderColor}`,
        padding: "10px 16px",
        fontSize: "0.8125rem",
      },
      head: {
        backgroundColor: surfaceSubtle,
        color: palette.text.secondary,
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        borderBottom: `1px solid ${borderColor}`,
      },
      sizeSmall: { padding: "8px 12px" },
    },

    MuiTableRow: {
      root: {
        "&:hover": { backgroundColor: palette.action.hover },
        "&:last-child > .MuiTableCell-root": { borderBottom: "none" },
      },
    },

    MuiChip: {
      root: { borderRadius: radius.sm, fontWeight: 600, fontSize: "0.75rem" },
      sizeSmall: { height: 22 },
      outlined: { borderColor },
    },

    MuiOutlinedInput: {
      root: {
        borderRadius: radius.md,
        backgroundColor: palette.background.paper,
        "& fieldset": { borderColor },
        "&:hover fieldset": { borderColor: isDark ? neutral[600] : neutral[300] },
      },
      input: { fontSize: "0.875rem" },
      inputMarginDense: { paddingTop: 9, paddingBottom: 9 },
    },

    MuiInputBase: {
      input: {
        "&::placeholder": { color: palette.text.hint, opacity: 1 },
      },
    },

    MuiInputLabel: { outlined: { fontSize: "0.875rem" } },

    MuiDialog: { paper: { borderRadius: radius.lg } },
    MuiDialogTitle: { root: { padding: "20px 24px 12px" } },
    MuiDialogContent: { root: { padding: "0 24px 8px" } },
    MuiDialogActions: {
      root: { padding: "16px 24px 20px", borderTop: `1px solid ${borderColor}` },
    },

    MuiTooltip: {
      tooltip: {
        backgroundColor: isDark ? neutral[700] : neutral[800],
        fontSize: "0.75rem",
        fontWeight: 500,
        borderRadius: radius.sm,
        padding: "6px 10px",
      },
      arrow: { color: isDark ? neutral[700] : neutral[800] },
    },

    MuiMenu: { paper: { borderRadius: radius.md, marginTop: 4 } },
    MuiMenuItem: { root: { fontSize: "0.875rem", minHeight: 38 } },

    MuiTab: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } },
    MuiTabs: { root: { minHeight: 44, borderBottom: `1px solid ${borderColor}` } },

    MuiDivider: { root: { backgroundColor: borderColor } },

    MuiCard: { root: { borderRadius: radius.lg, border: `1px solid ${borderColor}` } },
  };
};

/**
 * Constrói o tema completo do produto.
 *
 * @param {"light"|"dark"} mode
 * @param {object} [locale] pacote de locale do MUI (ex.: ptBR)
 */
export const buildTheme = (mode, locale) => {
  const isDark = mode === "dark";
  const palette = buildPalette(isDark);

  return createTheme(
    {
      palette,
      typography: buildTypography(),
      shape: { borderRadius: radius.md },
      spacing: spacingUnit,
      shadows: buildShadows(isDark),
      props: {
        MuiButton: { disableElevation: true },
        MuiPaper: { elevation: 0 },
        MuiTextField: { variant: "outlined", size: "small" },
        MuiFormControl: { variant: "outlined", size: "small" },
        MuiTooltip: { arrow: true },
        MuiLink: { underline: "hover" },
      },
      overrides: buildOverrides(palette, isDark),

      // Tokens expostos no tema para os componentes do design system.
      tokens: { neutral, brand, accent, semantic, radius, fontFamilyMono, layout },

      // Mantido por compatibilidade: várias páginas fazem
      // `...theme.scrollbarStyles`. O visual real vem do MuiCssBaseline acima.
      scrollbarStyles: {
        "&::-webkit-scrollbar": { width: 10, height: 10 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDark ? neutral[700] : neutral[300],
          borderRadius: radius.pill,
        },
      },
    },
    locale || {}
  );
};

export default buildTheme;
